import React, { useMemo, useState } from "react";
import { TrustPill } from "../TrustPill";
import type { ExecutiveSiteRow } from "../../lib/executive-seo-types";

interface Props {
  rows: ExecutiveSiteRow[];
}

type SortKey =
  | "domain"
  | "niche"
  | "gscImpressions30d"
  | "gscClicks30d"
  | "organicSessions30d"
  | "desktopTop10"
  | "desktopTop30"
  | "desktopTop100"
  | "realLeads30d";

export function SiteExecutiveTable({ rows }: Props): React.ReactElement {
  const [query, setQuery] = useState("");
  const [niche, setNiche] = useState("all");
  const [state, setState] = useState("all");
  const [trustFilter, setTrustFilter] = useState("all");
  const [phoneFilter, setPhoneFilter] = useState("all");
  const [signalOnly, setSignalOnly] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("gscImpressions30d");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const niches = useMemo(() => Array.from(new Set(rows.map((r) => r.niche).filter(Boolean))) as string[], [rows]);
  const states = useMemo(() => Array.from(new Set(rows.map((r) => r.state).filter(Boolean))) as string[], [rows]);
  const phoneStates = useMemo(() => Array.from(new Set(rows.map((r) => r.phoneState))), [rows]);

  const filtered = rows.filter((r) => {
    if (query && !r.domain.includes(query.toLowerCase())) return false;
    if (niche !== "all" && r.niche !== niche) return false;
    if (state !== "all" && r.state !== state) return false;
    if (phoneFilter !== "all" && r.phoneState !== phoneFilter) return false;
    if (trustFilter !== "all" && r.trustLabel?.level !== trustFilter) return false;
    if (signalOnly && r.realLeads30d === 0 && r.gscClicks30d === 0 && r.organicSessions30d === 0) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortKey] as number | string;
    const bv = b[sortKey] as number | string;
    if (typeof av === "number" && typeof bv === "number") {
      return sortDir === "asc" ? av - bv : bv - av;
    }
    return sortDir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });

  const toggleSort = (k: SortKey) => {
    if (k === sortKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  };

  const Header = (props: { k: SortKey; label: string; align?: "left" | "right" }) => (
    <th className={`px-3 py-2 text-${props.align ?? "left"} cursor-pointer select-none`} onClick={() => toggleSort(props.k)}>
      <span className="flex items-center gap-1 text-xs uppercase tracking-wider text-ink-600">
        {props.label}{sortKey === props.k && <span>{sortDir === "asc" ? "▲" : "▼"}</span>}
      </span>
    </th>
  );

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        <input
          type="search" placeholder="search domain…" value={query}
          onChange={(e) => setQuery(e.target.value.toLowerCase())}
          className="px-3 py-1.5 rounded border border-ink-200 text-sm w-48"
        />
        <select value={niche} onChange={(e) => setNiche(e.target.value)} className="px-2 py-1.5 rounded border border-ink-200 text-sm">
          <option value="all">all niches</option>
          {niches.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <select value={state} onChange={(e) => setState(e.target.value)} className="px-2 py-1.5 rounded border border-ink-200 text-sm">
          <option value="all">all states</option>
          {states.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={phoneFilter} onChange={(e) => setPhoneFilter(e.target.value)} className="px-2 py-1.5 rounded border border-ink-200 text-sm">
          <option value="all">all phone states</option>
          {phoneStates.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={trustFilter} onChange={(e) => setTrustFilter(e.target.value)} className="px-2 py-1.5 rounded border border-ink-200 text-sm">
          <option value="all">all trust levels</option>
          <option value="verified">verified</option>
          <option value="directional">directional</option>
          <option value="contaminated">contaminated</option>
          <option value="blocked">blocked</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={signalOnly} onChange={(e) => setSignalOnly(e.target.checked)} />
          has signal only
        </label>
        <div className="ml-auto text-sm text-ink-500 self-center">{sorted.length} of {rows.length} sites</div>
      </div>

      <div className="overflow-x-auto bg-white rounded-xl border border-ink-100 shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-ink-50">
            <tr>
              <Header k="domain" label="domain" />
              <Header k="niche" label="niche / city" />
              <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-ink-600">phone</th>
              <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-ink-600">posts</th>
              <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-ink-600">audit</th>
              <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-ink-600">idx %</th>
              <Header k="realLeads30d" label="leads 30d" align="right" />
              <Header k="gscImpressions30d" label="GSC imp 30d" align="right" />
              <Header k="gscClicks30d" label="GSC clk 30d" align="right" />
              <Header k="organicSessions30d" label="organic 30d" align="right" />
              <Header k="desktopTop10" label="d top10" align="right" />
              <Header k="desktopTop30" label="d top30" align="right" />
              <Header k="desktopTop100" label="d top100" align="right" />
              <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-ink-600">m top10/30/100</th>
              <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-ink-600">trust</th>
              <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-ink-600">next action</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.id} className="border-t border-ink-100 hover:bg-ink-50/50">
                <td className="px-3 py-2 font-mono text-xs">{r.domain}</td>
                <td className="px-3 py-2 text-xs">{r.niche} · {r.city}, {r.state}</td>
                <td className="px-3 py-2 text-xs">{r.phoneState}</td>
                <td className="px-3 py-2 text-right">{r.posts}</td>
                <td className={`px-3 py-2 text-xs ${r.auditStatus === "fail" ? "text-rose-700" : r.auditStatus === "warn" ? "text-amber-700" : "text-emerald-700"}`}>{r.auditStatus}</td>
                <td className="px-3 py-2 text-right text-xs">{r.indexedRatio == null ? "—" : `${(r.indexedRatio * 100).toFixed(0)}%`}</td>
                <td className={`px-3 py-2 text-right font-medium ${r.realLeads30d > 0 ? "text-emerald-700" : "text-ink-400"}`}>{r.realLeads30d}</td>
                <td className="px-3 py-2 text-right">{r.gscImpressions30d.toLocaleString()}</td>
                <td className={`px-3 py-2 text-right ${r.gscClicks30d > 0 ? "text-emerald-700 font-medium" : ""}`}>{r.gscClicks30d}</td>
                <td className="px-3 py-2 text-right">{r.organicSessions30d}</td>
                <td className="px-3 py-2 text-right">{r.desktopTop10}</td>
                <td className="px-3 py-2 text-right">{r.desktopTop30}</td>
                <td className="px-3 py-2 text-right">{r.desktopTop100}</td>
                <td className="px-3 py-2 text-right text-xs">{r.mobileTop10}/{r.mobileTop30}/{r.mobileTop100}</td>
                <td className="px-3 py-2"><TrustPill label={r.trustLabel} /></td>
                <td className="px-3 py-2 text-xs text-ink-700">{r.nextAction}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
